import type { PermissionStatus } from '@metro/location'
import type { MetroMapEvent } from './metroMapStore'
import type { SelectedStationSnapshot } from '../interaction/selectedStationSnapshot'

/**
 * Host-side dependencies the "my location" controller needs. Injected so
 * the §3.3 button contract is unit-testable without React / the browser
 * Geolocation API.
 */
export interface MyLocationDeps {
  /** Current OS permission status (no prompt). */
  getPermission: () => PermissionStatus
  /**
   * Whether the From slot is currently empty (no departure station set —
   * auto-managed default or user-cleared both count as empty). Half of the
   * re-fix canon "fill From only if BOTH From and To are empty" (see
   * {@link getToIsEmpty}).
   */
  getFromIsEmpty: () => boolean
  /**
   * Whether the To slot is currently empty. The re-fix canon only seeds
   * From=nearest when the user has started NO route at all (both slots
   * empty); once they've picked a destination (To set) the my-location
   * tap is recenter-only and must not touch From.
   */
  getToIsEmpty: () => boolean
  /**
   * Triggers the **real** browser permission prompt and resolves to the
   * derived status. The browser only shows its location prompt when
   * `navigator.geolocation.getCurrentPosition` is called — a bare
   * `navigator.permissions.query` never prompts. So this must be backed
   * by an actual fix attempt: a resolved fix means `granted`, a rejected
   * fix means `denied`.
   */
  requestPermissionViaFix: () => Promise<PermissionStatus>
  /**
   * Resolves the user's current nearest station as a map snapshot, or
   * `null` when no fix / no station could be resolved.
   */
  resolveNearestSnapshot: () => Promise<SelectedStationSnapshot | null>
  /** Centers the map camera on a station. */
  focusOn: (snapshot: { canvasX: number; canvasY: number }) => void
  /** Dispatches an event into the MetroMap store. */
  dispatchMap: (event: MetroMapEvent) => void
  /** Surfaces the location-denied modal. */
  showDeniedModal: () => void
}

/**
 * Handles a "my location" button tap, implementing the cross-platform
 * §3.3 contract:
 *
 * - `notDetermined` → fire the real browser permission prompt via
 *   `requestPermissionViaFix` (a status-only query never prompts on a
 *   `prompt`-state browser). Then continue on the derived status.
 * - `denied` / `restricted` → show the denied modal (OS will not re-prompt).
 * - `granted` → ALWAYS recenter on the nearest station. THEN, only when
 *   BOTH the From and To slots are empty (no route started), seed
 *   `from = nearest` via `RouteFromSelectedByGeolocation`. Otherwise the
 *   tap is recenter-only.
 *
 * Re-fix canon (user-validated on the live build): my-location NEVER opens
 * station details and NEVER auto-builds a route — it recenters the map,
 * and at most pre-fills the From slot on a fresh, empty route. The earlier
 * behavior (open `StationSelected`, fill on From-empty regardless of To)
 * was wrong; the camera recenter is the always-on primary action.
 */
export async function handleMyLocationRequested(deps: MyLocationDeps): Promise<void> {
  let permission = deps.getPermission()

  if (permission === 'notDetermined') {
    // A first-time user has a `prompt`-state browser. Querying status
    // (`navigator.permissions.query`) would just return `notDetermined`
    // and show nothing — the browser permission prompt ONLY appears when
    // `getCurrentPosition` runs. `requestPermissionViaFix` drives that
    // real prompt and reports `granted` (fix resolved) or `denied`
    // (fix rejected).
    permission = await deps.requestPermissionViaFix()
  }

  if (permission === 'denied' || permission === 'restricted') {
    deps.showDeniedModal()
    return
  }

  // `notDetermined` here would mean the prompt resolved to neither
  // grant nor deny — treat as a no-op (no fix to act on, no modal).
  if (permission !== 'granted') {
    return
  }

  const nearest = await deps.resolveNearestSnapshot()
  if (nearest === null) {
    return
  }

  // Recenter is the ALWAYS-on primary action of a my-location tap.
  deps.focusOn(nearest)

  // Seed From=nearest ONLY on a fresh route — both slots empty. If the
  // user has already set From OR To, the tap stays recenter-only and must
  // not touch the route. Never opens station details / auto-routes.
  if (!deps.getFromIsEmpty() || !deps.getToIsEmpty()) {
    return
  }

  deps.dispatchMap({ kind: 'RouteFromSelectedByGeolocation', station: nearest })
}
